import React, { useEffect, useState } from "react";
import { PageHeader } from "./DashboardLayout";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Paperclip } from "lucide-react";

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newImages, setNewImages] = useState([]);

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data || []);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const openTicket = async (id) => {
    setSelected(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const createTicket = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("subject", subject.trim());
      fd.append("message", message.trim());
      Array.from(newImages).forEach((f) => fd.append("images", f));
      await api.post("/support/tickets", fd);
      toast.success("Support ticket created.");
      setSubject(""); setMessage(""); setNewImages([]);
      try {
        await loadTickets();
      } catch {
        toast.info("Ticket created. Refresh once to see latest list.");
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!reply.trim() && images.length === 0) return;
    const fd = new FormData();
    fd.append("message", reply.trim());
    Array.from(images).forEach((f) => fd.append("images", f));
    setLoading(true);
    try {
      await api.post(`/support/tickets/${selected}/messages`, fd);
      setReply("");
      setImages([]);
      await openTicket(selected);
      await loadTickets();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Support" title="Tickets and replies." subtitle="Open support requests and chat with admin support." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 space-y-4">
          <form onSubmit={createTicket} className="bg-white rounded-3xl border border-cream-300 shadow-soft p-4 space-y-3">
            <p className="text-[12px] uppercase tracking-[0.18em] font-semibold text-ink-muted">New ticket</p>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40" />
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe your issue" rows={4} className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40" />
            <label className="flex items-center gap-2 cursor-pointer text-[12px] text-ink-muted hover:text-ink transition">
              <Paperclip size={13}/>
              <span>{newImages.length > 0 ? `${newImages.length} image${newImages.length > 1 ? "s" : ""} attached` : "Attach images (optional)"}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setNewImages(e.target.files || [])} />
            </label>
            <button disabled={loading} className="w-full bg-ink hover:bg-coral text-cream-100 rounded-full py-2.5 text-[13px] font-semibold transition disabled:opacity-60">
              {loading ? <span className="inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Creating…</span> : "Create ticket"}
            </button>
          </form>

          <div className="bg-white rounded-3xl border border-cream-300 shadow-soft overflow-hidden">
            <div className="px-4 py-3 border-b border-cream-300 text-[12px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Your tickets</div>
            <ul className="divide-y divide-cream-300 max-h-[420px] overflow-y-auto">
              {tickets.map((t) => (
                <li key={t.id}>
                  <button onClick={() => openTicket(t.id)} className={`w-full text-left px-4 py-3 hover:bg-cream-100 ${selected === t.id ? "bg-cream-100" : ""}`}>
                    <p className="text-[14px] font-semibold truncate">{t.subject}</p>
                    <p className="text-[12px] text-ink-muted">{t.status} · {t.updated_at ? new Date(t.updated_at).toLocaleString("en-BD") : ""}</p>
                  </button>
                </li>
              ))}
              {tickets.length === 0 && <li className="px-4 py-6 text-[13px] text-ink-muted">No tickets yet.</li>}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-3xl border border-cream-300 shadow-soft min-h-[520px] flex flex-col">
          {!selected ? (
            <div className="p-8 text-ink-muted text-[14px]">Select a ticket to view conversation.</div>
          ) : !detail ? (
            <div className="p-8 text-ink-muted inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading conversation…</div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-cream-300">
                <p className="font-semibold text-[15px]">{detail.ticket?.subject}</p>
                <p className="text-[12px] text-ink-muted">Status: {detail.ticket?.status}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {(detail.messages || []).map((m) => (
                  <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.sender_role === "admin" ? "bg-ink text-cream-100 ml-auto" : "bg-cream-100 text-ink"}`}>
                    {m.message && <p className="text-[13px] whitespace-pre-wrap">{m.message}</p>}
                    {Array.isArray(m.images) && m.images.length > 0 && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {m.images.map((img) => (
                          <a key={img} href={`/api/support/files/${img}`} target="_blank" rel="noreferrer" className="block">
                            <img src={`/api/support/files/${img}`} alt="attachment" className="w-full h-24 object-cover rounded-lg border border-black/10" />
                          </a>
                        ))}
                      </div>
                    )}
                    <p className={`text-[10px] mt-1 ${m.sender_role === "admin" ? "text-cream-100/70" : "text-ink-muted"}`}>{m.created_at ? new Date(m.created_at).toLocaleString("en-BD") : ""}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={sendReply} className="p-4 border-t border-cream-300 space-y-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Write a reply" className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px]" />
                <div className="flex items-center justify-between gap-3">
                  <label className="text-[12px] text-ink-muted inline-flex items-center gap-2 cursor-pointer">
                    <Paperclip size={13} /> Add images
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setImages(e.target.files || [])} />
                  </label>
                  <button disabled={loading || (!reply.trim() && images.length === 0)} className="bg-ink hover:bg-coral text-cream-100 rounded-full px-4 py-2 text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                    {loading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
